import { v4 as uuidv4 } from "uuid";
import { useMemo } from "react";
import { FormConfiguration } from "../../../shared/crud/form";
import { AccommodationForm } from "../../domain/Accommodation.entity";
import { defineMessage } from "react-intl";
import { messages } from "../../messages";
import { translationFieldConfig } from "../../../shared/widgets/translation";
import { usePropertyLocales } from "../../../../components/property-locale-provider";
import { HtmlEditorWidget } from "../../../shared/widgets/html";
import { imageManagerConfig } from "../../../shared/widgets/ImageManager";
import { useConfig } from "../../../../lib/config";
import OccupancyGenerator from "../../components/OccupancyGenerator";
import AccommodationBaseAdultsWidget from "../../components/AccommodationBaseAdultsWidget";
import { CategoryLimitsField } from "../../../extrasCategories/presentation/components/CategoryLimitsField";
import { CategoryLimitsFormData } from "../../../extrasCategories/presentation/types/CategoryLimitsTypes";

export interface CategoryLimitsFormOptions {
  sectionVisible: boolean;
  /**
   * Initial state of the section. As the schema default, rjsf fills it in on create without
   * calling `onChange`, so the form is not marked as changed on load.
   */
  initialState?: CategoryLimitsFormData;
}

const NO_CATEGORY_LIMITS: CategoryLimitsFormOptions = { sectionVisible: false };

export function useFormConfiguration(
  hasAccommodationModel: boolean,
  { sectionVisible, initialState }: CategoryLimitsFormOptions = NO_CATEGORY_LIMITS
) {
  const config = useConfig();
  const allowedLocales = usePropertyLocales();

  return useMemo<FormConfiguration<AccommodationForm>>(() => {
    return {
      __fieldOrder: [
        "ticker",
        "active",
        "type",
        "translationsList",
        "accommodationBaseAdults",
        "customOccupancy",
        // Only while the property is in the schema: rjsf rejects order entries it does not know.
        ...(sectionVisible ? (["categoryLimits"] as const) : []),
        "multimediaList",
        "*",
      ],
      __useProtoStyleArrays: true,
      id: {
        default: () => uuidv4(),
        hidden: true,
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({ id: "accommodation.ticker", defaultMessage: "Identifier" }),
        },
      },
      active: {
        hidden: true,
        default: () => true,
      },
      type: {
        placeholder: defineMessage({
          id: "accommodation.type.placeholder",
          defaultMessage: "Select type of accommodation",
        }),
        messages: {
          label: defineMessage({ id: "accommodation.type", defaultMessage: "type" }),
        },
        enumLabels: [
          messages.roomDenominationTypeUnit,
          messages.roomDenominationTypeRooms,
          messages.roomDenominationTypeApartments,
          messages.roomDenominationTypeBungalows,
          messages.roomDenominationTypeCabins,
          messages.roomDenominationTypeHouses,
          messages.roomDenominationTypeShipCabin,
          messages.roomDenominationTypeVillas,
          messages.roomDenominationTypeBeds,
          messages.roomDenominationTypePitches,
          messages.roomDenominationTypeHolidayVillages,
          messages.roomDenominationTypeMobileHome,
          messages.roomDenominationTypeGlamping,
          messages.roomDenominationTypeCaravans,
        ],
      },
      accommodationBaseAdults: {
        hidden: !hasAccommodationModel,
        fieldWidget: AccommodationBaseAdultsWidget,
        default: 0,
      },
      customOccupancy: {
        hidden: !hasAccommodationModel,
        fieldWidget: OccupancyGenerator,
      },
      occupancyCombinationsList: {
        hidden: true,
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["name", "description", "*"],
          name: {
            messages: {
              label: defineMessage({
                id: "accommodation.translation.name",
                defaultMessage: "Name",
              }),
            },
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "accommodation.translation.description",
                defaultMessage: "Description",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({
            id: "accommodation.translations",
            defaultMessage: "Translations",
          }),
        },
      },
      categoryLimits: {
        hidden: !sectionVisible,
        default: initialState,
        fieldWidget: CategoryLimitsField,
      },
      multimediaList: {
        ...imageManagerConfig({
          locales: allowedLocales,
          maxSizeMB: config.mediaMaxSizeMB,
          maxFiles: config.mediaMaxFiles,
          minWidth: config.mediaMinWidth,
          helpText: {
            id: messages.imagesHelpText.id,
            defaultMessage: messages.imagesHelpText.defaultMessage,
            values: {
              maxFiles: 10,
            },
          },
        }),
        messages: {
          label: defineMessage({ id: "accommodation.images", defaultMessage: "Images management" }),
        },
      },
      occupancy: {
        hidden: true,
      },
    };
  }, [
    allowedLocales,
    config.mediaMaxFiles,
    config.mediaMaxSizeMB,
    config.mediaMinWidth,
    hasAccommodationModel,
    sectionVisible,
    initialState,
  ]);
}
